<template>
  <div class="page-container">
    <div class="page-header">
      <h2>特殊规则配置</h2>
      <p>代持关系与特殊成交价配置；特殊价格保存或删除后，服务端会自动触发后台全量重算交易明细（与名册导入后一致），完成后股东交易、筹码成本等与当前配置一致。仍可使用下方「重新计算交易明细」在前台同步等待脚本跑完。</p>
    </div>

    <!-- Tab切换 -->
    <div class="tab-container">
      <el-tabs v-model="activeTab" type="border-card" @tab-change="onTabChange">
        <!-- 代持股份配置 -->
        <el-tab-pane label="代持股份配置" name="entrust">
          <div class="tab-content">
            <!-- 操作区域 -->
            <div class="action-section">
              <el-button type="primary" @click="openEntrustDialog()">添加代持关系</el-button>
              <el-button type="danger" @click="handleDeleteBatchEntrust" :disabled="selectedEntrustRows.length === 0">批量删除</el-button>
            </div>

            <!-- 说明 -->
            <div class="info-card">
              <div class="info-icon">💡</div>
              <div class="info-content">
                <div class="info-title">说明</div>
                <div class="info-text">
                  代持方（任意股东名）名义持有的股份中，按配置数量视为受益核心股东控制。代持方在持股≥代持股数的期数会扣减对应股数，全站该股东显示为扣减后股数；总股数不变。
                </div>
              </div>
            </div>

            <!-- 表格 -->
            <el-table 
              :data="entrustTableData" 
              border 
              stripe 
              style="width: 100%" 
              @selection-change="handleEntrustSelectionChange"
              v-loading="entrustLoading"
            >
              <el-table-column type="selection" width="55" />
              <el-table-column prop="entrust_id" label="编号" width="140" show-overflow-tooltip />
              <el-table-column prop="core_name" label="核心股东（受益方）" width="160">
                <template #default="{ row }">
                  <el-tag type="danger">{{ row.core_name }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="entrusted_holder" label="代持方" min-width="180">
                <template #default="{ row }">
                  <span class="holder-name">{{ row.entrusted_holder }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="entrusted_shares" label="代持股数" width="120" align="right">
                <template #default="{ row }">
                  <span class="number">{{ (row.entrusted_shares || 0).toLocaleString() }}</span> 股
                </template>
              </el-table-column>
              <el-table-column label="操作" width="150">
                <template #default="{ row }">
                  <el-button size="small" type="primary" @click="openEntrustDialog(row)">编辑</el-button>
                  <el-button size="small" type="danger" @click="handleDeleteEntrust(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-tab-pane>

        <!-- 特殊交易价格配置 -->
        <el-tab-pane label="特殊交易价格" name="price">
          <div class="tab-content">
            <!-- 操作区域 -->
            <div class="action-section action-section-wrap">
              <el-button type="primary" @click="openPriceDialog()">添加特殊价格</el-button>
              <el-button type="danger" @click="handleDeleteBatchPrice" :disabled="selectedPriceRows.length === 0">批量删除</el-button>
              <el-button @click="loadPriceList">刷新列表</el-button>
              <el-button type="warning" plain :loading="recalcLoading" @click="triggerTransactionDetailRecalc">
                重新计算交易明细
              </el-button>
            </div>

            <!-- 说明 -->
            <div class="info-card">
              <div class="info-icon">💡</div>
              <div class="info-content">
                <div class="info-title">说明</div>
                <div class="info-text">
                  按「名册期日期 + 证券账户或股东姓名」在全量重算脚本中覆盖该期该户的成交价（不直接改库内旧 fact；保存/删除配置后会自动触发后台重算刷新 fact）。仍可在上方点击「重新计算交易明细」做前台同步等待。
                  若库内仍是早期占位表（无主键 price_id），首次进入本页加载列表时服务端会自动删旧表并按当前结构重建。
                </div>
              </div>
            </div>

            <!-- 表格 -->
            <el-table 
              :data="priceTableData" 
              border 
              stripe 
              style="width: 100%" 
              @selection-change="handlePriceSelectionChange"
              v-loading="priceLoading"
            >
              <el-table-column type="selection" width="55" />
              <el-table-column prop="price_id" label="编号" width="140" show-overflow-tooltip />
              <el-table-column prop="trade_date" label="名册日期" width="120" />
              <el-table-column prop="seller" label="卖方" width="150">
                <template #default="{ row }">
                  <span class="seller-name">{{ row.seller }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="buyer" label="买方" width="150">
                <template #default="{ row }">
                  <span class="buyer-name">{{ row.buyer }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="account_code" label="证券账户" width="120" show-overflow-tooltip />
              <el-table-column prop="holder_name" label="明细股东" min-width="120" show-overflow-tooltip />
              <el-table-column prop="shares" label="交易股数" width="100" align="right">
                <template #default="{ row }">
                  <span class="number">{{ (row.shares || 0).toLocaleString() }}</span>股
                </template>
              </el-table-column>
              <el-table-column prop="actual_price" label="实际成交价" width="100" align="right">
                <template #default="{ row }">
                  <span class="price-value">{{ (row.actual_price || 0).toFixed(2) }}</span>元
                </template>
              </el-table-column>
              <el-table-column prop="system_price" label="系统计算价" width="100" align="right">
                <template #default="{ row }">
                  <span class="price-system">{{ (row.system_price || 0).toFixed(2) }}</span>元
                </template>
              </el-table-column>
              <el-table-column prop="price_diff" label="价差" width="80" align="center">
                <template #default="{ row }">
                  <span :class="['price-diff', row.price_diff > 0 ? 'up' : 'down']">
                    {{ row.price_diff > 0 ? '+' : '' }}{{ (row.price_diff || 0).toFixed(2) }}元
                  </span>
                </template>
              </el-table-column>
              <el-table-column prop="trade_type" label="交易类型" width="100">
                <template #default="{ row }">
                  <el-tag :type="getTradeTypeTag(row.trade_type)">{{ getTradeTypeText(row.trade_type) }}</el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="120">
                <template #default="{ row }">
                  <el-button size="small" type="primary" @click="openPriceDialog(row)">编辑</el-button>
                  <el-button size="small" type="danger" @click="handleDeletePrice(row)">删除</el-button>
                </template>
              </el-table-column>
            </el-table>
          </div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 代持股份弹窗 -->
    <el-dialog v-model="entrustDialogVisible" :title="entrustDialogTitle" width="500px" @close="resetEntrustForm">
      <el-form :model="entrustForm" :rules="entrustRules" ref="entrustFormRef" label-width="120px">
        <el-form-item label="核心股东（受益方）" prop="core_shareholder_id">
          <HolderSuggestInput
            v-model="entrustForm.core_shareholder_id"
            type="core"
            placeholder="汉字/拼音/首字母"
            trigger-on-focus
            style="width: 100%"
            @select="(item) => { entrustForm.core_name = item.label || item.name || '' }"
          />
        </el-form-item>
        <el-form-item label="代持方" prop="entrusted_holder">
          <HolderSuggestInput
            v-model="entrustForm.entrusted_holder"
            type="all"
            value-key="label"
            placeholder="输入代持方股东名称（汉字/拼音/首字母）"
          />
        </el-form-item>
        <el-form-item label="代持股数" prop="entrusted_shares">
          <el-input-number v-model="entrustForm.entrusted_shares" :min="0" :step="10000" style="width: 100%" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="entrustDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSaveEntrust">保存</el-button>
      </template>
    </el-dialog>

    <!-- 特殊交易价格弹窗 -->
    <el-dialog v-model="priceDialogVisible" :title="priceDialogTitle" width="520px" @close="resetPriceForm">
      <el-form :model="priceForm" :rules="priceRules" ref="priceFormRef" label-width="130px">
        <el-form-item label="名册日期" prop="trade_date">
          <el-date-picker
            v-model="priceForm.trade_date"
            type="date"
            placeholder="与名册批次日期一致"
            style="width: 100%"
            value-format="YYYY-MM-DD"
            @change="() => refreshPricePreview(true)"
          />
        </el-form-item>
        <el-form-item label="证券账户" prop="account_code">
          <el-input
            v-model="priceForm.account_code"
            placeholder="可选，填写后与明细账户精确匹配"
            clearable
            @blur="() => refreshPricePreview(true)"
          />
        </el-form-item>
        <el-form-item label="卖方" prop="seller">
          <HolderSuggestInput v-model="priceForm.seller" type="all" value-key="label" placeholder="汉字/拼音/首字母" />
        </el-form-item>
        <el-form-item label="买方" prop="buyer">
          <HolderSuggestInput v-model="priceForm.buyer" type="all" value-key="label" placeholder="汉字/拼音/首字母" />
        </el-form-item>
        <el-form-item label=" ">
          <el-button type="default" @click="() => refreshPricePreview(false)">从交易明细拉取系统价与股数</el-button>
        </el-form-item>
        <el-form-item v-if="priceForm.holder_name" label="匹配明细股东">
          <span class="price-system">{{ priceForm.holder_name }}</span>
        </el-form-item>
        <el-form-item label="交易股数" prop="shares">
          <el-input-number v-model="priceForm.shares" :min="0" :step="10000" style="width: 100%" />
        </el-form-item>
        <el-form-item label="实际成交价" prop="actual_price">
          <el-input-number v-model="priceForm.actual_price" :min="0" :precision="2" :step="0.01" style="width: 100%" />
        </el-form-item>
        <el-form-item label="系统计算价" prop="system_price">
          <el-input-number v-model="priceForm.system_price" :min="0" :precision="2" :step="0.01" style="width: 100%" />
        </el-form-item>
        <el-form-item label="交易类型" prop="trade_type">
          <el-select v-model="priceForm.trade_type" placeholder="请选择交易类型" style="width: 100%">
            <el-option label="司法拍卖" value="auction" />
            <el-option label="大宗交易" value="block" />
            <el-option label="协议转让" value="transfer" />
            <el-option label="股权激励" value="incentive" />
            <el-option label="其他" value="other" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="priceDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="priceSaveLoading" @click="handleSavePrice">保存</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, nextTick } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import axios from 'axios'
import HolderSuggestInput from '@/components/HolderSuggestInput.vue'

// Tab状态
const activeTab = ref('entrust')
const entrustFormRef = ref(null)
const priceFormRef = ref(null)
const recalcLoading = ref(false)
const priceSaveLoading = ref(false)

onMounted(() => {
  loadEntrustList()
  loadPriceList()
})

const onTabChange = (paneName) => {
  if (paneName === 'price') loadPriceList()
}

const triggerTransactionDetailRecalc = async () => {
  try {
    await ElMessageBox.confirm(
      '将触发后台全量重算交易明细，耗时随数据量增加。是否继续？',
      '重新计算交易明细',
      { type: 'warning', confirmButtonText: '开始计算', cancelButtonText: '取消' }
    )
  } catch {
    return
  }
  recalcLoading.value = true
  try {
    await axios.post('/api/transaction_detail/calculate/')
    ElMessage.success('已提交计算，请稍后刷新股东交易等页面')
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '提交失败')
  } finally {
    recalcLoading.value = false
  }
}

// 代持列表 API
const loadEntrustList = async () => {
  entrustLoading.value = true
  try {
    const res = await axios.get('/api/special/entrust/')
    const list = (res.data.data || res.data || [])
    entrustTableData.value = list.map(item => ({
      entrust_id: item.entrust_id,
      core_shareholder_id: item.core_shareholder_id,
      core_name: item.core_name || '',
      entrusted_holder: item.holder_name || '',
      entrusted_shares: item.entrust_shares || 0
    }))
  } catch (e) {
    console.error('加载代持列表失败:', e)
    const msg = e.response?.data?.message || e.message || '加载代持列表失败'
    ElMessage.error(msg)
    entrustTableData.value = []
  } finally {
    entrustLoading.value = false
  }
}

// 代持股份数据
const entrustLoading = ref(false)
const entrustTableData = ref([])
const selectedEntrustRows = ref([])
const entrustDialogVisible = ref(false)
const entrustDialogTitle = ref('添加代持关系')
const entrustForm = reactive({
  entrust_id: null,
  core_shareholder_id: '',
  core_name: '',
  entrusted_holder: '',
  entrusted_shares: 0
})
const entrustRules = {
  core_shareholder_id: [{ required: true, message: '请选择受益核心股东', trigger: 'change' }],
  entrusted_holder: [{ required: true, message: '请输入代持方股东名称', trigger: 'blur' }],
  entrusted_shares: [{ required: true, message: '请输入代持股数', trigger: 'blur' }]
}

// 特殊交易价格数据
const priceLoading = ref(false)
const priceTableData = ref([])

const mapPriceApiRow = (item) => ({
  price_id: item.price_id,
  trade_date: item.batch_date || item.trade_date || '',
  seller: item.seller_holder_name || item.seller || '',
  buyer: item.buyer_holder_name || item.buyer || '',
  shares: item.txn_volume_snapshot ?? item.shares ?? 0,
  actual_price: item.special_price ?? item.actual_price ?? 0,
  system_price: item.system_price ?? 0,
  price_diff: item.price_diff ?? (Number(item.special_price || 0) - Number(item.system_price || 0)),
  trade_type: item.trade_type || '',
  account_code: item.account_code || '',
  holder_name: item.holder_name || ''
})

const loadPriceList = async () => {
  priceLoading.value = true
  try {
    const res = await axios.get('/api/special/transaction-price/')
    const list = res.data.data || res.data || []
    priceTableData.value = (Array.isArray(list) ? list : []).map(mapPriceApiRow)
  } catch (e) {
    console.error('加载特殊价格失败:', e)
    const msg = e.response?.data?.message || e.message || '加载特殊价格失败'
    ElMessage.error(msg)
    priceTableData.value = []
  } finally {
    priceLoading.value = false
  }
}
const selectedPriceRows = ref([])
const priceDialogVisible = ref(false)
const priceDialogTitle = ref('添加特殊价格')
const priceForm = reactive({
  price_id: null,
  trade_date: '',
  account_code: '',
  holder_name: '',
  seller: '',
  buyer: '',
  shares: 0,
  actual_price: 0,
  system_price: 0,
  trade_type: ''
})
const priceRules = {
  trade_date: [{ required: true, message: '请选择名册日期', trigger: 'change' }],
  seller: [{ required: true, message: '请输入卖方名称', trigger: 'blur' }],
  buyer: [{ required: true, message: '请输入买方名称', trigger: 'blur' }],
  actual_price: [{ required: true, message: '请输入实际成交价', trigger: 'blur' }],
  trade_type: [{ required: true, message: '请选择交易类型', trigger: 'change' }]
}

// 方法
const handleEntrustSelectionChange = (selection) => {
  selectedEntrustRows.value = selection
}

const handlePriceSelectionChange = (selection) => {
  selectedPriceRows.value = selection
}

const openEntrustDialog = (row = null) => {
  if (row) {
    entrustDialogTitle.value = '编辑代持关系'
    entrustForm.entrust_id = row.entrust_id
    entrustForm.core_shareholder_id = row.core_shareholder_id
    entrustForm.core_name = row.core_name
    entrustForm.entrusted_holder = row.entrusted_holder
    entrustForm.entrusted_shares = row.entrusted_shares || 0
  } else {
    entrustDialogTitle.value = '添加代持关系'
    resetEntrustForm()
  }
  entrustDialogVisible.value = true
}

const resetEntrustForm = () => {
  entrustForm.entrust_id = null
  entrustForm.core_shareholder_id = ''
  entrustForm.core_name = ''
  entrustForm.entrusted_holder = ''
  entrustForm.entrusted_shares = 0
}

const handleSaveEntrust = async () => {
  try {
    await entrustFormRef.value?.validate?.()
  } catch {
    return
  }
  if (!entrustForm.core_shareholder_id || !entrustForm.entrusted_holder) {
    ElMessage.error('请填写受益核心股东与代持方')
    return
  }
  const payload = {
    entrust_holder_name: entrustForm.entrusted_holder.trim(),
    entrust_shares: Number(entrustForm.entrusted_shares) || 0
  }
  try {
    if (entrustForm.entrust_id) {
      await axios.put(`/api/special/entrust/update/${entrustForm.entrust_id}/`, payload)
      ElMessage.success('更新成功')
    } else {
      await axios.post('/api/special/entrust/create/', {
        core_shareholder_id: entrustForm.core_shareholder_id,
        ...payload
      })
      ElMessage.success('添加成功')
    }
    entrustDialogVisible.value = false
    loadEntrustList()
  } catch (e) {
    const msg = e.response?.data?.message || e.message || '保存失败'
    ElMessage.error(msg)
  }
}

const handleDeleteEntrust = (row) => {
  ElMessageBox.confirm('确认删除该代持关系？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      await axios.delete(`/api/special/entrust/delete/${row.entrust_id}/`)
      ElMessage.success('删除成功')
      loadEntrustList()
    } catch (e) {
      ElMessage.error(e.response?.data?.message || '删除失败')
    }
  }).catch(() => {})
}

const handleDeleteBatchEntrust = () => {
  if (selectedEntrustRows.value.length === 0) {
    ElMessage.warning('请选择要删除的记录')
    return
  }
  ElMessageBox.confirm(`确认删除选中的 ${selectedEntrustRows.value.length} 条代持关系？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      for (const row of selectedEntrustRows.value) {
        await axios.delete(`/api/special/entrust/delete/${row.entrust_id}/`)
      }
      selectedEntrustRows.value = []
      ElMessage.success('批量删除成功')
      loadEntrustList()
    } catch (e) {
      ElMessage.error(e.response?.data?.message || '删除失败')
    }
  }).catch(() => {})
}

const openPriceDialog = (row = null) => {
  if (row) {
    priceDialogTitle.value = '编辑特殊价格'
    priceForm.price_id = row.price_id
    priceForm.trade_date = row.trade_date || ''
    priceForm.account_code = row.account_code || ''
    priceForm.holder_name = row.holder_name || ''
    priceForm.seller = row.seller || ''
    priceForm.buyer = row.buyer || ''
    priceForm.shares = row.shares || 0
    priceForm.actual_price = row.actual_price || 0
    priceForm.system_price = row.system_price || 0
    priceForm.trade_type = row.trade_type || ''
  } else {
    priceDialogTitle.value = '添加特殊价格'
    resetPriceForm()
  }
  priceDialogVisible.value = true
}

const resetPriceForm = () => {
  priceForm.price_id = null
  priceForm.trade_date = ''
  priceForm.account_code = ''
  priceForm.holder_name = ''
  priceForm.seller = ''
  priceForm.buyer = ''
  priceForm.shares = 0
  priceForm.actual_price = 0
  priceForm.system_price = 0
  priceForm.trade_type = ''
}

/** @param {boolean} silent 为 true 时仅静默填表（换日期/失焦时），不弹成功提示 */
const refreshPricePreview = async (silent = false) => {
  if (!priceForm.trade_date) return
  const params = new URLSearchParams({
    batch_date: priceForm.trade_date,
    account_code: (priceForm.account_code || '').trim(),
    seller: (priceForm.seller || '').trim(),
    buyer: (priceForm.buyer || '').trim()
  })
  try {
    const res = await axios.get(`/api/special/transaction-price/preview/?${params.toString()}`)
    const d = res.data?.data
    const msg = res.data?.message
    if (d) {
      priceForm.system_price = Number(d.system_price) || 0
      priceForm.shares = Number(d.txn_volume) || 0
      priceForm.holder_name = d.holder_name || ''
      if (!(priceForm.account_code || '').trim() && d.account_code) {
        priceForm.account_code = d.account_code
      }
      if (!silent) {
        ElMessage.success('已从交易明细匹配到一行，系统价与股数已填入')
      }
    } else if (msg && !silent) {
      ElMessage.warning(msg)
    }
  } catch (e) {
    console.warn('预览系统价失败', e)
    if (!silent) {
      ElMessage.warning(e.response?.data?.message || '预览失败，请核对名册日期与股东/账户')
    }
  }
}

const handleSavePrice = async () => {
  try {
    await priceFormRef.value?.validate?.()
  } catch {
    return
  }
  if (!priceForm.seller || !priceForm.buyer) {
    ElMessage.error('请填写卖方、买方')
    return
  }
  const holderName =
    (priceForm.holder_name || '').trim() ||
    (priceForm.seller || '').trim() ||
    (priceForm.buyer || '').trim()
  const acc = (priceForm.account_code || '').trim()
  if (!acc && !holderName) {
    ElMessage.error('请填写证券账户，或点击「从交易明细拉取」匹配股东')
    return
  }
  const body = {
    batch_date: priceForm.trade_date,
    special_price: priceForm.actual_price,
    system_price: priceForm.system_price,
    trade_type: priceForm.trade_type,
    seller_holder_name: (priceForm.seller || '').trim(),
    buyer_holder_name: (priceForm.buyer || '').trim(),
    txn_volume_snapshot: priceForm.shares,
    account_code: acc,
    holder_name: (priceForm.holder_name || '').trim() || (priceForm.seller || '').trim()
  }
  priceSaveLoading.value = true
  try {
    if (priceForm.price_id) {
      const res = await axios.put(`/api/special/transaction-price/update/${priceForm.price_id}/`, body)
      ElMessage.success(res.data?.message || '更新成功')
    } else {
      const res = await axios.post('/api/special/transaction-price/create/', body)
      ElMessage.success(res.data?.message || '添加成功')
    }
    priceDialogVisible.value = false
    await loadPriceList()
    await nextTick()
  } catch (e) {
    ElMessage.error(e.response?.data?.message || e.message || '保存失败')
  } finally {
    priceSaveLoading.value = false
  }
}

const handleDeletePrice = (row) => {
  ElMessageBox.confirm('确认删除该特殊价格配置？', '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      const res = await axios.delete(`/api/special/transaction-price/delete/${row.price_id}/`)
      ElMessage.success(res.data?.message || '删除成功')
      loadPriceList()
    } catch (e) {
      ElMessage.error(e.response?.data?.message || '删除失败')
    }
  }).catch(() => {})
}

const handleDeleteBatchPrice = () => {
  if (selectedPriceRows.value.length === 0) {
    ElMessage.warning('请选择要删除的记录')
    return
  }

  ElMessageBox.confirm(`确认删除选中的 ${selectedPriceRows.value.length} 条特殊价格配置？`, '提示', {
    confirmButtonText: '确定',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(async () => {
    try {
      for (const row of selectedPriceRows.value) {
        await axios.delete(`/api/special/transaction-price/delete/${row.price_id}/`)
      }
      selectedPriceRows.value = []
      ElMessage.success(
        '批量删除完成；每条删除均已触发后台重算，请稍后刷新股东交易/筹码页查看最新结果'
      )
      loadPriceList()
    } catch (e) {
      ElMessage.error(e.response?.data?.message || '删除失败')
    }
  }).catch(() => {})
}

const getTradeTypeTag = (type) => {
  const map = {
    auction: 'danger',
    block: 'warning',
    transfer: 'primary',
    incentive: 'success',
    other: 'info'
  }
  return map[type] || 'info'
}

const getTradeTypeText = (type) => {
  const map = {
    auction: '司法拍卖',
    block: '大宗交易',
    transfer: '协议转让',
    incentive: '股权激励',
    other: '其他'
  }
  return map[type] || '其他'
}
</script>

<style scoped>
.page-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100vh;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  font-size: 24px;
  font-weight: 600;
  color: #0F172A;
  margin-bottom: 8px;
}

.page-header p {
  font-size: 14px;
  color: #64748B;
}

.action-section {
  margin-bottom: 16px;
  display: flex;
  gap: 12px;
}

.action-section-wrap {
  flex-wrap: wrap;
  align-items: center;
}

.info-card {
  display: flex;
  background: #EFF6FF;
  border: 1px solid #BFDBFE;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.info-icon {
  font-size: 20px;
  margin-right: 12px;
}

.info-title {
  font-size: 14px;
  font-weight: 600;
  color: #1E40AF;
  margin-bottom: 4px;
}

.info-text {
  font-size: 13px;
  color: #3B82F6;
  line-height: 1.5;
}

.tab-content {
  padding: 16px 0;
}

.holder-name,
.seller-name,
.buyer-name {
  font-weight: 500;
  color: #0F172A;
}

.number {
  font-family: 'DIN Condensed', 'Roboto', sans-serif;
  font-weight: 600;
}

.price-value {
  color: #DC2626;
  font-weight: 600;
}

.price-system {
  color: #64748B;
}

.price-diff {
  font-weight: 600;
}

.price-diff.up {
  color: #DC2626;
}

.price-diff.down {
  color: #10B981;
}

.autocomplete-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.autocomplete-item .item-name {
  flex: 1;
}

:deep(.el-tabs__content) {
  padding: 0 16px;
}

:deep(.el-table) {
  font-size: 14px;
}

:deep(.el-table .cell) {
  padding-left: 8px;
  padding-right: 8px;
}
</style>
